import { ConfigurationService } from '@common';

import { RenewContextServiceBase } from '../../../../baseBtl/context/child/renew-context.service';
import { Configurator } from '../../../../catalog/products/interface/configurable';
import { Product } from '../../../../catalog/products/product';
import { Promotion } from '../../../../checkout/cart/promotions/promotion.class';
import { IPromotions } from '../../../../checkout/cart/promotions/promotion.interface';
import { CustomerContextService } from '../../../../context/child/customer-context.service';
import { SchemeBase } from '../../../checkout/cart/scheme.class';
import { ProductContextBase } from '../../../context/child/product-context.service';
import { SimBase } from '../equipement/sim';
import { SubscriptionBase } from '../subscription';
import { OptionBase } from './option';

export class PlanBase extends SubscriptionBase {
    public price: number;
    public realPrice: number;
    public priceClient: number;
    public priceNoClient: number;
    public data_envelope: string;
    public commercial_range: string;
    public isDiscountPrice: boolean;
    public banner: string;
    public isMainPrice: boolean = false;
    public hasDifferentPriceAmount: boolean = false;
    public bannerCustomer: boolean = false;

    private _configService: ConfigurationService;
    protected get configService(): ConfigurationService {
        return this._configService;
    }

    public get promoDuree(): number {
        if (this.price !== this.oldPrice) {
            const promoDuration = this.getFirstMinDuration(this.promotion);
            const portaDuration = this.getFirstMinDuration(this.porta);
            const customerDuration = this.getFirstMinDuration(this.customer);

            if (this.promotion && promoDuration) {
                return promoDuration;
            } else if (this.porta && portaDuration) {
                return portaDuration;
            } else if (this.customer && customerDuration) {
                return customerDuration;
            }
        }
        return null;
    }

    public hasPromo(): boolean {
        return this.getEligiblePromosList().length > 0;
    }

    public get promoClientDuree(): number {
        if (this.priceClient !== this.oldPrice) {
            return this.promoDuree;
        }
        return null;
    }

    public get promoNoClientDuree(): number {
        if (this.priceNoClient !== this.baseprice) {
            return this.promoDuree;
        }
        return null;
    }

    public get promoRealDuree(): number {
        if (this.realPrice !== this.baseprice) {
            return this.promoDuree;
        }
        return null;
    }

    public get promoPortaReduction() {
        if (this.porta && this.porta.length) {
            return this.porta[0].reduction;
        }
        return null;
    }

    public get promoPortaDuree() {
        if (this.porta && this.porta.length) {
            return this.porta[0].duree;
        }
        return null;
    }

    public get context(): ProductContextBase {
        return this._context;
    }

    public set context(productContextService: ProductContextBase) {
        this._context = productContextService;
        this.updatePrice();
    }

    public get clientContexts(): { [key: string]: any } {
        const renewContext: RenewContextServiceBase =
            this.context.contextService.get<RenewContextServiceBase>(RenewContextServiceBase);
        const isCustomer: boolean = this.customerContext !== null && this.customerContext.contracts.length > 0;

        return {
            renewContext,
            isCustomer,
        };
    }

    protected applyAml(): boolean {
        this.price = this.price - this.price / 10;
        return true;
    }

    public updateSchemeOnRemove(scheme: SchemeBase): SchemeBase {
        let products: Product[] = scheme.getProductsByType(OptionBase);
        products = products.concat(scheme.getProductsByType(SimBase));
        if (products.length > 0) {
            products.forEach((product: Product) => {
                scheme.remove(product.uniqueId);
            });
        }
        return scheme;
    }

    public getEligiblePromosList(): IPromotions[] {
        this.updatePromotion();
        return []
            .concat(this.promotion, this.porta, this.customer, this.locales, this.convergence)
            .filter((promo) => promo !== null);
    }

    // @TODO: Check out this rule since ity' different from the v1 version
    protected getFirstMinDuration(promotions: IPromotions[]): number {
        const promos = promotions ? promotions.filter((promo) => promo.duree > 0) : [];

        if (!promos.length) {
            return 0;
        }

        return promos.reduce((min, promotion: IPromotions) => Math.min(min, promotion.duree), promos[0].duree);
    }

    private totalWithoutIncitation(basePrice: number): number {
        const customerConfigurator: Configurator = this._context.scheme.customer;
        const customerContext: CustomerContextService =
            this.context.contextService.get<CustomerContextService>(CustomerContextService);
        const isCustomer: boolean = customerContext != null && customerContext.contracts.length > 0;

        let price: number = basePrice;
        let clientPromos = [].concat(this.convergence, this.customer).filter((promo) => promo !== null);

        // Case: Connected and check on contract type
        if (customerContext || isCustomer || !customerConfigurator) {
            clientPromos = clientPromos.filter((promo: IPromotions) =>
                promo.conditions.callOperator === true && promo.produits.includes(this.data.gencode.toString())
                    ? true
                    : Promotion.hasContractType(promo.conditions.client.contrats, customerContext.contracts),
            );
        }

        price -= clientPromos.reduce((acc: number, currentPromo: IPromotions) => acc + currentPromo.reduction, 0);

        return price > 0 ? price : 0;
    }

    protected updatePrice(): void {
        this.price = super.getPrice();
        this.priceClient = super.getPrice();
        this.priceNoClient = super.getPrice();

        this.updatePromotion();
        this.updateIncitation(); // @ASK Why here and not at the end since the priceClient is used in function logic ?

        this.priceNoClient = this.calculatePriceNoClientTotal();
        this.priceClient = this.calculatePriceClientTotal();

        if (this.priceClient === this.priceNoClient && this.data.obligation) {
            this.isDiscountPrice = false;
        }
        this.updateBasicPrices();

        // @ASK: Why ?
        this.updateIncitationBanner();
        super.updatePrice();
    }

    private calculatePriceNoClientTotal(): number {
        let price: number = this.baseprice;

        if (this.isBasicPromoAllowed()) {
            price -= this.promosTotal;
        }
        if (this.isPortaPromoAllowed()) {
            price -= this.promosPortaTotal;
        }
        if (this.isLocalesPromoAllowed()) {
            price -= this.promosLocaleTotal;
        }
        if ((!this.hasContract && this.isCustomerPromoAllowed()) || this.forcedCustomerPromo) {
            price -= this.promosClientTotal;
        }

        price -= this.promosFakeConvergenceTotal();

        return price;
    }

    private calculatePriceClientTotal(): number {
        const customerConfigurator: Configurator = this._context.scheme.customer;

        let price: number = this.baseprice;

        if (this.isPortaPromoAllowed()) {
            price -= this.promosPortaTotal;
        }
        if (this.isLocalesPromoAllowed()) {
            price -= this.promosLocaleTotal;
        }
        if (this.isConvergencePromoAllowed()) {
            price -= this.promosConvergenceTotal(!!customerConfigurator);
        }
        if (this.isCustomerPromoAllowed()) {
            price -= this.promosCustomerTotal;
        }
        return price;
    }

    private calculateRealPriceTotal(): number {
        let price: number = this.baseprice;

        if (this.isPortaPromoAllowed()) {
            price -= this.promosPortaTotal;
        }
        if (this.isLocalesPromoAllowed()) {
            price -= this.promosLocaleTotal;
        }

        price = this.totalWithoutIncitation(price);
        return price > 0 ? price : 0;
    }

    private isPortaPromoAllowed(): boolean {
        return this.porta && this.porta.length > 0 && this._context.scheme.porta !== undefined;
    }

    private isLocalesPromoAllowed(): boolean {
        return this.locales && this._context.scheme.porta !== undefined && this._context.scheme.locales !== undefined;
    }

    private isConvergencePromoAllowed(): boolean {
        return this.convergence && this.promoCustomerActive();
    }

    private isBasicPromoAllowed(): boolean {
        return this.promotion && this.promotion.length > 0;
    }

    private isCustomerPromoAllowed(): boolean {
        return (this.customer && this.promoCustomerActive()) || this.forcedCustomerPromo;
    }

    private get isCustomer(): boolean {
        return this.hasIncitation || (this.customerContext && this.customerContext.contracts.length > 0);
    }

    private promoCustomerActive(): boolean {
        return this.hasIncitation || this.isCustomer || this._context.scheme.customer !== undefined;
    }

    private updateBasicPrices(): void {
        const renewContext: RenewContextServiceBase =
            this.context.contextService.get<RenewContextServiceBase>(RenewContextServiceBase);
        const customerConfigurator: Configurator = this._context.scheme.customer;
        const isCustomer: boolean = this.customerContext != null && this.customerContext.contracts.length > 0;

        this.price = this.promoCustomerActive() ? this.priceClient : this.priceNoClient;
        this.oldPrice =
            this.convergence && this.promoCustomerActive()
                ? this.baseprice - this.promosConvergenceTotal(true)
                : this.baseprice;
        // Price with incitation exluded
        this.realPrice = isCustomer || customerConfigurator ? this.calculateRealPriceTotal() : this.priceNoClient;
        // Price with incitation included
        this.realOldPrice =
            this.convergence && (isCustomer || customerConfigurator)
                ? this.baseprice - this.promosConvergenceTotal(!!customerConfigurator)
                : this.baseprice - this.promosFakeConvergenceTotal();

        // @TODO: Eliminate these by checking on total sum
        this.price = this.price > 0 ? this.price : 0;
        this.oldPrice = this.oldPrice > 0 ? this.oldPrice : 0;
        this.priceClient = this.priceClient > 0 ? this.priceClient : 0;
        this.priceNoClient = this.priceNoClient > 0 ? this.priceNoClient : 0;

        if (renewContext && renewContext.skuOffre === this.gencode) {
            this.price = renewContext.amountPaid;
            this.oldPrice = this.price;
        }
    }

    protected updateIncitation() {
        let allClientPromos: IPromotions[] = [];

        if (this.convergence) {
            allClientPromos = allClientPromos.concat(this.convergence);
        }
        if (this.customer) {
            allClientPromos = allClientPromos.concat(this.customer);
        }
        this.hasIncitation =
            allClientPromos.filter(
                (promo: IPromotions) => promo.conditions.client !== null && promo.conditions.client.incitation.enabled,
            ).length > 0;

        this.hasContract =
            allClientPromos
                .filter((promo: IPromotions) =>
                    promo.conditions.callOperator ? true : !promo.conditions.client.contrats.includes('ALL'),
                )
                .filter((promo: IPromotions) => {
                    const isAppliable = promo.produits.includes(this.data.gencode.toString());
                    return (
                        (promo.conditions.callOperator && isAppliable) ||
                        (promo.conditions.client !== null && promo.hasContractType)
                    );
                }).length === allClientPromos.length && allClientPromos.length > 0;

        this.isDiscountPrice =
            this.hasContract ||
            allClientPromos.filter((promo: IPromotions) => {
                const isAppliable = promo.produits.includes(this.data.gencode.toString());
                if (
                    promo.conditions.callOperator &&
                    isAppliable &&
                    this.customerContext &&
                    !this.customerContext.contracts.length &&
                    (this.customerContext.isPremiumForPromo() || this.customerContext.isStandardForPromo())
                ) {
                    this.forcedCustomerPromo = this.configService.getData()?.global?.convergence_lot_2.status;
                }
                return (
                    isAppliable &&
                    ((promo.conditions.client !== null && promo.hasContractType) || this.forcedCustomerPromo)
                );
            }).length === allClientPromos.length;

        this.isMainPrice = this.customerContext == null || this.priceClient > 0;
    }

    protected updateIncitationBanner() {
        const customerContext: CustomerContextService =
            this.context.contextService.get<CustomerContextService>(CustomerContextService);

        const promoCustomer: IPromotions = this.customer
            ? this.customer.find(
                  (promo: IPromotions) => promo.conditions.client && promo.conditions.client.incitation.enabled,
              )
            : null;
        let promoConvergence: IPromotions = this.convergence
            ? this.convergence.find(
                  (promo: IPromotions) => promo.conditions.client && promo.conditions.client.incitation.enabled,
              )
            : null;
        const isAppliableConvergence =
            promoConvergence &&
            promoConvergence.conditions &&
            promoConvergence.conditions.callOperator &&
            promoConvergence.produits.includes(this.data.gencode.toString());
        const isAppliableCustomer =
            promoCustomer &&
            promoCustomer.conditions &&
            promoCustomer.conditions.callOperator &&
            promoCustomer.produits.includes(this.data.gencode.toString());

        if (!this.obligation && promoCustomer && customerContext != null && !this.hasContract) {
            return;
        }

        if (isAppliableConvergence || isAppliableCustomer) {
            this.forcedCustomerPromo = true;
        }

        if (
            this.customerContext &&
            isAppliableCustomer &&
            (this.customerContext.isPremiumForPromo() || this.customerContext.isStandardForPromo())
        ) {
            this.forcedCustomerPromo = true;
        }

        if (
            (((customerContext == null || customerContext.contracts.length === 0) && this.hasIncitation) ||
                (this.obligation &&
                    customerContext != null &&
                    customerContext.contracts.length > 0 &&
                    !this.hasContract &&
                    this.hasIncitation)) &&
            !this.forcedCustomerPromo
        ) {
            promoConvergence = this.convergence
                ? this.convergence.find(
                      (promo: IPromotions) => promo.conditions.client && promo.conditions.client.incitation.enabled,
                  )
                : null;
            if (promoCustomer) {
                this.banner = promoCustomer.conditions.client.incitation.banner;
                this.bannerCustomer = true;
            } else if (promoConvergence) {
                this.banner = promoConvergence.conditions.client.incitation.banner;

                if (
                    isAppliableConvergence &&
                    this.customerContext &&
                    (this.customerContext.isPremiumForPromo() || this.customerContext.isStandardForPromo())
                ) {
                    this.forcedCustomerPromo = true;
                    if (customerContext && customerContext.contracts.length) {
                        this.forcedCustomerPromo = false;
                    }
                    this.banner = promoConvergence.titre;
                }

                this.bannerCustomer = true;
            }
        } else {
            if (this.promotion && this.promotion.length > 0) {
                this.banner = this.promotion[0].titre;
                this.bannerCustomer = false;
            } else if (
                (this.convergence && this.convergence.length > 0 && this.hasCompatibleContext(this.convergence)) ||
                isAppliableConvergence
            ) {
                this.banner = this.convergence[0].titre;
                this.bannerCustomer = true;
            } else if (
                (this.customer && this.customer.length > 0 && this.hasCompatibleContext(this.customer)) ||
                isAppliableCustomer
            ) {
                this.banner = this.customer[0].titre;
                this.bannerCustomer = true;
            }
        }
    }

    private hasCompatibleContext(promotions: IPromotions[]): boolean {
        const customerContext: CustomerContextService =
            this.context.contextService.get<CustomerContextService>(CustomerContextService);

        if (customerContext) {
            return (
                promotions[0].hasContractType &&
                Promotion.hasContractType(promotions[0].conditions.client.contrats, customerContext.contracts)
            );
        } else {
            return true;
        }
    }
}
